import {
  TIME_ZONE_RELATIONSHIP,
  TIME_ZONE_RELATIONSHIP_ABBREVIATED,
  TIME_ZONE_RELATIONSHIP_GENERIC,
  BROWSER_TIME_ZONE_RELATIONSHIP_ABBREVIATED,
  BROWSER_TIME_ZONE_RELATIONSHIP_GENERIC,
} from 'src/constants/formOptions';

/**
 * Given a pair of IANA⇄label tuples and one member of the pair,
 * returns the tuple or empty array.
 */
export const getTimeZoneByRelationship = ([byFirst, bySecond] = []) => TIME_ZONE_RELATIONSHIP.find(
  ([timeZoneFirst, timeZoneSecond]) => (byFirst && timeZoneFirst === byFirst) || (bySecond && timeZoneSecond === bySecond)
) || [];

/**
 * List of all "second" values in TIME_ZONE_RELATIONSHIP
 */
export const getSecondTypeTimeZones = () => TIME_ZONE_RELATIONSHIP.map(([, timeZoneSecond]) => timeZoneSecond);

/**
 * List of all abbreviated labels from TIME_ZONE_RELATIONSHIP_ABBREVIATED
 */
export const getSecondTypeTimeZonesAbbreviated = () => TIME_ZONE_RELATIONSHIP_ABBREVIATED.map(([, timeZoneSecond]) => timeZoneSecond);

/**
 * List of all generic labels from TIME_ZONE_RELATIONSHIP_GENERIC
 */
export const getSecondTypeTimeZonesGeneric = () => TIME_ZONE_RELATIONSHIP_GENERIC.map(([, timeZoneSecond]) => timeZoneSecond);

/**
 * Map a full IANA name (e.g. “America/New_York”) to its label using
 * the given relationships array; if not found, fall back to the
 * last path segment (e.g. “New York”).
 */
export function mapTimeZoneFull(fullZone, mapEntries) {
  if (!fullZone) return '';
  const map = Object.fromEntries(mapEntries);
  if (map[fullZone]) return map[fullZone];
  return fullZone.split('/').pop().replace('_', ' ');
}

/**
 * Map any full IANA name to a pretty label using the given
 * relationship table, falling back to the last slash‐segment.
 *
 * @param {string} fullZone  e.g. "America/New_York"
 * @param {[string,string][]} mapEntries  e.g. TIME_ZONE_RELATIONSHIP_GENERIC
 * @returns {string}  e.g. "Eastern Time"
 */
export function getGenericTimeZone(fullZone, mapEntries) {
  if (!fullZone) return '';
  const map = Object.fromEntries(mapEntries);
  return map[fullZone] || fullZone.split('/').pop().replace(/_/g, ' ');
}

/**
 * Given a user‐configured IANA (e.g. from their profile), return
 * the “generic” label (e.g. “Eastern Time”).
 */
export function getUserGenericTimeZone(fullZone) {
  return getGenericTimeZone(fullZone, TIME_ZONE_RELATIONSHIP_GENERIC);
}

/**
 * Given an IANA name (from user-settings or the browser), returns its two-letter code,
 * falling back to the last path segment if unmapped.
 */
export function getAbbreviatedTimeZone(fullZone) {
  // try your North-America map first
  const userMap = Object.fromEntries(TIME_ZONE_RELATIONSHIP_ABBREVIATED);
  if (userMap[fullZone]) return userMap[fullZone];

  // then fall back to the broader browser map
  const browserMap = Object.fromEntries(BROWSER_TIME_ZONE_RELATIONSHIP_ABBREVIATED);
  if (browserMap[fullZone]) return browserMap[fullZone];

  // and finally just “de-slash” things like “Europe/London” → “London”
  return fullZone.split('/').pop().replace('_', ' ');
}

/**
 * Safely grab the browser’s current IANA time-zone identifier
 * (or empty string if unavailable).
 */
export function getBrowserIana() {
  return typeof window !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : '';
}

/**
 * For the browser’s current IANA, return its “generic” label
 * (e.g. “Eastern Time”).
 */
export function getBrowserGenericTimeZone(fullZone) {
  return getGenericTimeZone(fullZone, BROWSER_TIME_ZONE_RELATIONSHIP_GENERIC);
}
